import React, { ReactNode } from 'react'
import { Label } from '../form-label'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'

export { TextInput } from './text-input'
export { Textarea } from './textarea'
export { JenkinsThresholdInput } from './jenkins-threshold-input'
import { monotonic } from '../../util'

const getErrorID = monotonic()

/**
 * Props that aren't otherwise explained are simply passed as attributes
 * to the input element
 */
export interface FormTextProps {
  /**
   * Used to fit the content to it's container
   */
  readonly expand?: boolean

  readonly 'aria-labelledby'?: string
  readonly 'data-testid'?: string
  readonly disabled?: boolean
  readonly maxLength?: number
  readonly readOnly?: boolean
  readonly name?: string
  readonly onFocus?: (
    event?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  readonly onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  readonly required?: boolean
  readonly showRequiredIndicator?: boolean
  readonly value?: string | number | ReadonlyArray<string>

  /**
   * Toggles an EDITED indicator on the field
   */
  readonly edited?: boolean

  /**
   * When provided, this activates the error styling of the controller
   * and the text will appear as amplyfing information beneath the control.
   */
  readonly errorMessage?: string

  /**
   * The id of the input element. Note: this should always be provided in tests
   */
  readonly id?: string

  /**
   * Text describing the input element. This will be used as the content
   * for the label element unless the renderLabel prop is used
   */
  readonly label?: string

  /**
   * onChange event handler for the input element. It will be
   * passed the value of the input as an argument.
   */
  readonly onChange?: (string) => void

  /**
   * This is passed as the placeholder prop to input element so long
   * as the TextField component is not disabled.
   */
  readonly placeholder?: string

  /**
   * If provided this callback will render the contents of a help tooltip. Also,
   * a help icon will appear as part of the control's label that activates the
   * tooltip when hovered.
   */
  readonly renderHelpContent?: () => ReactNode

  /**
   * Can be used to render an alternate label or exclude the label element
   * altogether if the callback returns null/undefined. If this callback is
   * defined it will be passed the value of the label prop as the first argument
   * and the input element id as the second argument.
   */
  readonly renderLabel?: (string, id) => ReactNode

  readonly tabIndex?: number

  /**
   * Optional CSS Styling for the primary form text container
   */
  readonly classNameFormTextPrimaryContainer?: string

  /**
   * Optional CSS Styling for the secondary form text container
   */
  readonly classNameFormTextSecondaryContainer?: string
}

type ChildrenProps = FormTextProps & {
  readonly 'aria-describedby'?: string
}

type FormTextContainerProps = FormTextProps & {
  readonly children: (props: ChildrenProps) => ReactNode
}

function buildFormTextContainerStyle(expand: boolean, optionalStyle?: string) {
  const defaultStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
    ${expand ? `height: 100%;` : undefined};
  `

  // eslint-disable-next-line unicorn/prefer-ternary
  if (optionalStyle) {
    return cx(defaultStyle, optionalStyle)
  } else {
    return defaultStyle
  }
}

export function FormTextContainer({
  children,
  expand = false,
  classNameFormTextPrimaryContainer,
  classNameFormTextSecondaryContainer,
  ...props
}: FormTextContainerProps) {
  const {
    id,
    errorMessage,
    renderLabel,
    label,
    disabled,
    renderHelpContent,
    required,
    edited,
    showRequiredIndicator
  } = props

  const errorID = `${id ? id : `textarea-${getErrorID()}`}-error-message`
  const ariaDescribedBy = errorMessage ? errorID : undefined

  const labelNode = renderLabel ? (
    renderLabel(label, id)
  ) : label ? (
    <Label
      htmlFor={id}
      edited={!disabled && edited}
      renderHelpContent={renderHelpContent}
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      className={css`
        ${expand ? `flex: 1;` : undefined};
      `}
    >
      {label}
    </Label>
  ) : undefined

  return (
    <div
      className={buildFormTextContainerStyle(
        expand,
        classNameFormTextPrimaryContainer
      )}
    >
      {labelNode}
      <div
        className={cx(
          css`
            position: relative;
            ${expand ? `flex: 7;` : undefined};
          `,
          classNameFormTextSecondaryContainer
        )}
      >
        {children({ ['aria-describedby']: ariaDescribedBy, ...props })}
      </div>
      {errorMessage && (
        <div
          className={css`
            color: ${theme.color.red50};
            font-size: 12px;
            margin-top: 3px;
          `}
          id={errorID}
        >
          {errorMessage}
        </div>
      )}
    </div>
  )
}
